// src/redux/services/supportTicketApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

export const supportTicketApi = createApi({
  reducerPath: "supportTicketApi",
  tagTypes: ["SupportTickets", "SupportMeta"],
  baseQuery,
  endpoints: (builder) => ({
    // ✅ Ticket list
    getSupportTickets: builder.query({
      query: ({ search = "", status = "", scope = "created_by" } = {}) => {
        const qs = new URLSearchParams();
        if (search) qs.set("search", search);
        if (status) qs.set("status", status);
        if (scope) qs.set("scope", scope);
        return `/support-tickets?${qs.toString()}`;
      },
      providesTags: (result) =>
        result?.tickets
          ? [
              ...result.tickets.map((t) => ({ type: "SupportTickets", id: t._id })),
              { type: "SupportTickets", id: "LIST" },
            ]
          : [{ type: "SupportTickets", id: "LIST" }],
      transformResponse: (response) => {
        const r = response?.data || response;
        if (Array.isArray(r)) return { tickets: r };
        return r || { tickets: [] };
      },
    }),

    getSupportTicketById: builder.query({
      query: ({ ticketId, scope = "created_by" }) =>
        `/support-tickets/${ticketId}?scope=${encodeURIComponent(scope)}`,
      providesTags: (result, error, arg) => [{ type: "SupportTickets", id: arg.ticketId }],
      transformResponse: (response) => {
        const r = response?.data || response;
        return r?.ticket || r || null;
      },
    }),

    // ✅ Types dropdown
    getSupportTicketTypes: builder.query({
      query: () => "/support-tickets/types",
      providesTags: [{ type: "SupportMeta", id: "TYPES" }],
      transformResponse: (response) => {
        const r = response?.data || response;
        return r?.types || r || [];
      },
    }),

getMyDevicesForSupport: builder.query({
  query: () => "/support-tickets/devices",
  providesTags: [{ type: "SupportMeta", id: "DEVICES" }],
  transformResponse: (response) => {
    const r = response?.data || response;
    return r?.devices || [];
  },
}),


    // ✅ Technician list (for assignment)
    getAssignableTechnicians: builder.query({
      query: () => "/support-tickets/technicians",
      providesTags: [{ type: "SupportMeta", id: "TECHS" }],
      transformResponse: (response) => {
        const r = response?.data || response;
        return r?.technicians || r || [];
      },
    }),

    // ✅ Create ticket (multipart)
    createSupportTicket: builder.mutation({
      query: (formData) => ({
        url: "/support-tickets",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "SupportTickets", id: "LIST" }],
    }),

    updateSupportTicket: builder.mutation({
      query: ({ ticketId, formData }) => ({
        url: `/support-tickets/${ticketId}`,
        method: "PUT",
        body: formData,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "SupportTickets", id: arg.ticketId },
        { type: "SupportTickets", id: "LIST" },
      ],
    }),

    // ✅ Assign ticket to technician
    assignSupportTicket: builder.mutation({
      query: ({ ticketId, technicianId }) => ({
        url: `/support-tickets/${ticketId}/assign`,
        method: "PUT",
        body: { technicianId },
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "SupportTickets", id: arg.ticketId },
        { type: "SupportTickets", id: "LIST" },
      ],
    }),

    // ✅ Update status (optional)
    updateSupportTicketStatus: builder.mutation({
      query: ({ ticketId, status }) => ({
        url: `/support-tickets/${ticketId}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "SupportTickets", id: arg.ticketId },
        { type: "SupportTickets", id: "LIST" },
      ],
    }),
    createSupportTechnician: builder.mutation({
  query: (body) => ({
    url: "/support-tickets/technicians",
    method: "POST",
    body,
  }),
  invalidatesTags: [{ type: "SupportMeta", id: "TECHS" }],
}),

  }),
});

export const {
  useGetSupportTicketsQuery,
  useGetSupportTicketByIdQuery,
  useGetSupportTicketTypesQuery,
  useGetMyDevicesForSupportQuery,
  useGetAssignableTechniciansQuery,
  useCreateSupportTicketMutation,
  useUpdateSupportTicketMutation,
  useAssignSupportTicketMutation,
  useUpdateSupportTicketStatusMutation,
  useCreateSupportTechnicianMutation
} = supportTicketApi;
